/**
 * ShareCourseButton.jsx — Bouton flottant "Partager ce cours"
 * Génère un QR code avec l'URL du cours + ?share=c2r pour permettre à des
 * élèves NON inscrits d'accéder au cours via le QR partagé par leur moniteur.
 *
 * VISIBILITÉ (Phase 32 — Mode Teacher Public) :
 *  - Sur les cours protégés SHARE_ROUTES → toujours visible aux enseignants
 *  - Sur les autres pages contenu (magazine, plateau, permis-am, etc.) →
 *    visible si `teacherMode` actif (rôle enseignant OU jeton ?teacher=1)
 *
 * S'appuie sur `useTeacherMode()` pour gérer le jeton URL et sessionStorage.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Share2, X, Copy, Check, MessageCircle } from 'lucide-react';
import { useTeacherMode } from '../hooks/useTeacherMode';

const SHARE_TOKEN = 'c2r';

/* Préfixes de routes sur lesquels le bouton apparaît en mode enseignant.
 * Toute page /magazine/*, /permis-am/*, /plateau/*, /circulation/*, /cours/*,
 * /formation-125/*, /livret*, /briefing-*, /videos/*, /enquetes/*, /fiches/* */
const TEACHER_MODE_PATH_PREFIXES = [
  '/cours/', '/livret', '/briefing-', '/formation-125/',
  '/permis-am', '/plateau', '/circulation',
  '/magazine', '/dossier', '/enquetes', '/videos',
  '/fiches/', '/ressources-pedagogiques', '/lexique',
];

/* Routes exactes toujours partageables (même pour un enseignant non connecté
 * qui viendrait par exemple d'un lien direct). */
const ALWAYS_SHARE_ROUTES = [
  '/cours/premiere-circulation-a2', '/cours/deuxieme-circulation-a2', '/cours/troisieme-circulation-a2',
  '/cours/quatrieme-circulation-a2', '/cours/cinquieme-circulation-a2', '/cours/sixieme-circulation-a2',
  '/cours/premiere-plateau-a2', '/cours/deuxieme-plateau-a2', '/cours/troisieme-plateau-a2', '/cours/quatrieme-plateau-a2',
  '/cours/permis-am-journee-1', '/cours/permis-am-journee-2', '/cours/permis-am-sensibilisation',
  '/formation-125/comprendre', '/formation-125/maitriser', '/formation-125/anticiper',
  '/livret-plateau', '/livret-circulation', '/livret',
  '/briefing-circulation', '/briefing-a2',
];

export function ShareCourseButton() {
  const location = useLocation();
  const { teacherMode, isTeacherModeToken, disableTeacherMode } = useTeacherMode();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Uniquement pour les enseignants OU en mode teacher token
  if (!teacherMode) return null;

  const path = location.pathname;
  const isAlwaysShareRoute = ALWAYS_SHARE_ROUTES.some((r) => path === r || path.startsWith(r + '/'));
  const isTeacherModeRoute = TEACHER_MODE_PATH_PREFIXES.some((p) => path.startsWith(p));

  // Masquer sur le back-office et les pages non-contenu
  const isBackOffice = path.startsWith('/moniteur') || path.startsWith('/admin') || path.startsWith('/secretariat') || path.startsWith('/eleve');
  const isAuthRoute = path.startsWith('/login') || path.startsWith('/register') || path.startsWith('/mot-de-passe');
  if (isBackOffice || isAuthRoute) return null;

  // Si pas dans les préfixes autorisés, on cache
  if (!isAlwaysShareRoute && !isTeacherModeRoute) return null;

  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}${path}?share=${SHARE_TOKEN}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      /* ignore */
    }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`📚 Voici le cours à consulter : ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Cours Code 2 Roues',
          text: 'Voici le cours à consulter pour ta formation moto',
          url: shareUrl,
        });
      } catch (e) {
        /* user cancelled */
      }
    }
  };

  return (
    <>
      {/* Bouton flottant en bas à droite */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest text-xs px-4 py-3 shadow-2xl active:scale-95 transition-all"
        style={{ boxShadow: '0 10px 30px -8px rgba(220,38,38,0.6)' }}
        aria-label="Partager ce cours avec mes élèves"
        data-testid="share-course-btn"
      >
        <Share2 className="w-4 h-4" />
        <span className="hidden sm:inline">Partager</span>
      </button>

      {/* Modale QR */}
      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setOpen(false)}
          role="dialog"
          data-testid="share-course-modal"
        >
          <div
            className="bg-white rounded-3xl p-6 sm:p-7 max-w-sm w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center active:scale-95 transition"
              aria-label="Fermer"
              data-testid="share-course-close"
            >
              <X className="w-4 h-4 text-slate-700" strokeWidth={2.5} />
            </button>

            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-600 text-[10px] font-black uppercase tracking-widest mb-2">
                <Share2 className="w-3 h-3" /> Partage moniteur
              </div>
              <h2
                className="text-xl sm:text-2xl font-black text-slate-900 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Partager ce cours
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Tes élèves scannent le QR ci-dessous · aucun compte requis
              </p>
            </div>

            {/* QR Code */}
            <div className="bg-white rounded-2xl p-4 border-2 border-slate-100 flex items-center justify-center mb-4" data-testid="share-course-qr">
              <QRCodeSVG
                value={shareUrl}
                size={220}
                level="M"
                includeMargin={false}
                fgColor="#0f172a"
                bgColor="#ffffff"
              />
            </div>

            {/* URL affichée */}
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5 mb-3 flex items-center gap-2">
              <div className="flex-1 min-w-0 text-[10px] text-slate-600 font-mono truncate select-all">
                {shareUrl}
              </div>
              <button
                type="button"
                onClick={copyLink}
                className={`flex-shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                  copied
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-900 text-white hover:bg-slate-700 active:scale-95'
                }`}
                data-testid="share-course-copy"
              >
                {copied ? <><Check className="w-3 h-3" /> Copié</> : <><Copy className="w-3 h-3" /> Copier</>}
              </button>
            </div>

            {/* Boutons de partage rapide */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={shareWhatsApp}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase tracking-widest text-[10px] px-3 py-2.5 active:scale-95 transition"
                data-testid="share-course-whatsapp"
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
              {typeof navigator !== 'undefined' && typeof navigator.share === 'function' ? (
                <button
                  type="button"
                  onClick={nativeShare}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 hover:bg-slate-700 text-white font-black uppercase tracking-widest text-[10px] px-3 py-2.5 active:scale-95 transition"
                  data-testid="share-course-native"
                >
                  <Share2 className="w-3.5 h-3.5" /> Autre app
                </button>
              ) : (
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 font-black uppercase tracking-widest text-[10px] px-3 py-2.5 active:scale-95 transition"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier lien
                </button>
              )}
            </div>

            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-[10px] text-amber-900 leading-relaxed">
              <strong>💡 À savoir :</strong> le lien fonctionne pour un élève NON inscrit tant qu&apos;il garde son onglet ouvert. Après fermeture, il devra créer un compte.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ShareCourseButton;
